// The room in the pause menu: invite code, players (the owner can kick),
// changing the map (owner) or calling a vote, and the running vote.

import { useState } from 'preact/hooks';
import { bindLabel } from '../../game/binds';
import { stripColors } from '../etColors';
import { useApp } from '../context';
import { net, settings, toast } from '../state';
import { Button } from './Button';
import { Icon } from './Icon';
import { Panel } from './Panel';

export function VoteStatus() {
  const app = useApp();
  const room = net.value.room;
  const binds = settings.value.binds;
  const v = room?.vote;
  if (!v) return null;
  const title = net.value.maps.find((m) => m.name === v.map)?.longname;
  return (
    <div class="vote-status" role="status">
      <div>
        <span class="eyebrow">Vote</span> change map to <b>{title ? stripColors(title) : v.map}</b> <span class="muted">({v.map}, called by {v.by})</span>
      </div>
      <div class="vote-count mono">
        ✓ {v.yes} / {v.needed} needed · ✗ {v.no}
      </div>
      <div class="row-buttons">
        <Button variant="primary" size="sm" hint={bindLabel(binds, 'voteYes')} onClick={() => app.vote(true)}>
          Yes
        </Button>
        <Button variant="secondary" size="sm" hint={bindLabel(binds, 'voteNo')} onClick={() => app.vote(false)}>
          No
        </Button>
      </div>
    </div>
  );
}

export function RoomPanel() {
  const app = useApp();
  const n = net.value;
  const room = n.room;
  const me = n.client?.id;
  const [map, setMap] = useState('');
  if (!room) return null;
  const owner = !room.official && room.ownerId === me;
  const others = n.maps.filter((m) => m.name !== room.map).sort((a, b) => stripColors(a.longname ?? a.name).localeCompare(stripColors(b.longname ?? b.name)));
  const chosen = map && others.some((m) => m.name === map) ? map : (others[0]?.name ?? '');

  const copy = () => {
    navigator.clipboard?.writeText(room.code).then(
      () => toast(`Invite code ${room.code} copied`, 'good'),
      () => toast(`Invite code: ${room.code}`, 'info'),
    );
  };

  return (
    <Panel
      title={room.name}
      class="room-panel"
      actions={
        <span class="chips">
          {room.official && <span class="chip static chip-info">Official</span>}
          {room.private && (
            <span class="chip static">
              <Icon name="lock" size={12} /> Private
            </span>
          )}
        </span>
      }
    >
      <div class="invite">
        <span class="field-label">Invite code</span>
        <span class="invite-code mono">{room.code}</span>
        <Button variant="ghost" size="sm" icon="copy" onClick={copy}>
          Copy
        </Button>
      </div>

      <h3 class="section-title">
        Players <span class="muted">· {room.members.length}/{room.maxPlayers}</span>
      </h3>
      <ul class="member-list">
        {room.members.map((m) => (
          <li key={m.id}>
            <span class="member-name">
              {m.id === room.ownerId && (
                <span class="owner-mark" title="Room owner">
                  <Icon name="crown" size={14} />
                </span>
              )}
              {m.name}
              {m.id === me && <span class="muted"> (you)</span>}
            </span>
            {m.spectating && <span class="chip static">Spectating</span>}
            <span class="row-buttons">
              {m.id !== me && !m.spectating && (
                <Button variant="ghost" size="sm" icon="eye" onClick={() => app.spectate(m.id)} aria-label={`Watch ${m.name}`}>
                  Watch
                </Button>
              )}
              {owner && m.id !== me && (
                <Button variant="ghost" size="sm" onClick={() => app.kick(m.id)} aria-label={`Kick ${m.name}`}>
                  Kick
                </Button>
              )}
            </span>
          </li>
        ))}
      </ul>

      <VoteStatus />

      {others.length > 0 && !room.vote && (
        <div class="map-change">
          <label class="stack-field">
            <span class="field-label">{owner ? 'Change map' : 'Vote for another map'}</span>
            <div class="select">
              <select value={chosen} onChange={(e) => setMap((e.currentTarget as HTMLSelectElement).value)}>
                {others.map((m) => (
                  <option key={m.name} value={m.name}>
                    {stripColors(m.longname ?? m.name)} ({m.name})
                  </option>
                ))}
              </select>
            </div>
          </label>
          {owner ? (
            <Button variant="secondary" onClick={() => app.setRoomMap(chosen)}>
              Change
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => app.voteMap(chosen)}>
              Call vote
            </Button>
          )}
        </div>
      )}
    </Panel>
  );
}
